/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useState } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { CustomInputText, CustomInputWrap, CustomTextarea } from 'components/inputs/CustomInput';
import { ContentSection } from 'components/layouts/ContentSection';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ContentFlex } from 'components/layouts/ContentFlex';
import ApprovalTemplateManagementGrid from 'pages/admin/approvaltemplatemanagement/ApprovalTemplateManagementGrid';
import { ApprovalTemplate } from 'models/admin/ApprovalTemplate';
import {
  createApprovalTemplate,
  getApprovalTemplate,
  modifyApprovalTemplate,
} from 'apis/admin/Approval';
import CrossEditor from 'components/crosseditor/CrossEditor';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { useCommonModal } from 'hooks/useCommonModal';
import { Code } from 'models/common/CommonCode';
import { CrudCode } from 'models/common/Edit';
import { useTranslation } from 'react-i18next';
import {
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from '../../components/layouts/ContentLayout';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { IconButton } from 'components/buttons/IconSVG';
import { getApprovalRuleMasters } from '../../apis/admin/ApprovalRule';
import { Button, FormControl } from '@mui/material';
import { tb } from '../../components/layouts/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import { ComboBox } from '../../components/selects/ComboBox';
import { CommonUtil } from '../../utils/CommonUtil';
import { useMessageBar } from '../../hooks/useMessageBar';
import { getNotificationGroups } from '../../apis/admin/Notification';
import { CommonYN } from '../../models/common/Common';
import { common } from '@mui/material/colors';
import { isDisabled } from '@testing-library/user-event/dist/utils';

/**
 * 결재양식기본정보관리
 * @constructor
 */
export const ApprovalTemplateManagementPage = () => {
  const { t } = useTranslation();

  const [condition, setCondition] = useState<string>('');
  const [aprTplSearchParamData, setAprTplSearchParamData] = useState<string>('');

  const prsDescEditorRef = useRef<CrossEditor>(null);
  const wcstDescEditorRef = useRef<CrossEditor>(null);

  const editorLoadCount = 0;

  const [errors, setErrors] = useState<any>({});
  const [code, setCode] = useState<any>();

  const [aprTplInfoData, setAprTplInfoData] = useState<ApprovalTemplate>({
    aprRuleId: '',
    aprTplId: '',
    intgAprTpCd: '',
    aprTplNm: '',
    sortOrd: 0,
    aprTplDesc: '',
    useYn: 'Y',
    prsDesc: '',
    wcstUseYn: 'Y',
    wcstDesc: '',
    notfUseYn: 'Y',
    ntdkId: 0,
    mgrUseYn: 'Y',
    crudKey: CrudCode.CREATE,
  });

  const [rowData, setRowData] = useState<ApprovalTemplate[]>([]);
  const [selectIntgAprTpCd, setSelectIntgAprTpCd] = useState<Code[]>([]);
  const [selectAprRuleId, setSelectAprRuleId] = useState<Code[]>([]);

  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();

  useEffect(() => {
    getCommonCodes();
  }, []);

  const getCommonCodes = async () => {
    const intgAprTpCdData = await getCommonCodeNames('INTG_APR_TP_CD');
    const useYn: Code[] = await getCommonCodeNames('YN_FLAG');
    // const asstRvwRltDivsCd2: Code[] = await getCommonCodeNamesCondition({
    //   optValCtn1: 'Y',
    //   cmnGrCd: 'ASST_RVW_RLT_DIVS_CD',
    // });

    const aprRuleData = await getApprovalRuleMasters('', '');
    const aprRuleList = aprRuleData.map((i) => ({
      cmnCd: i.aprRuleId,
      cmnCdNm: i.aprRuleNm,
    }));

    setSelectAprRuleId(
      aprRuleData.map((i) => ({
        cmnCd: i.aprRuleId,
        cmnCdNm: i.aprRuleNm,
      }))
    );
    setSelectIntgAprTpCd(intgAprTpCdData);

    const notificationGroups = await getNotificationGroups({ useYn: CommonYN.Y });
    const notificationList = notificationGroups.map((i) => ({
      cmnCd: i.ntdkId,
      cmnCdNm: i.ntdkNm,
    }));

    setCode({
      intgAprTpCdData: intgAprTpCdData, // 전자결재유형
      notificationGroups: notificationList, // 통보처
      aprRuleData: aprRuleList, //결재규칙
      useYn: useYn, //사용여부
    });
  };

  const newRow: ApprovalTemplate = {
    aprRuleId: '', //결재규칙ID
    aprTplId: '', //결재양식ID
    intgAprTpCd: 'TYPE3',
    aprTplNm: '', //결재양식명
    sortOrd: 0,
    aprTplDesc: '', //양식설명
    useYn: 'Y',
    prsDesc: '', //프로세스설명
    wcstUseYn: 'N', //동의서사용여부
    wcstDesc: '', //동의서설명
    notfUseYn: 'N', //통보처사용여부
    ntdkId: 0, //통보처ID
    mgrUseYn: 'Y', //담당자 사용여부
    crudKey: CrudCode.CREATE,
  };
  const btnReload = () => {
    setCondition(''); //양식명 초기화
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      btnSearch();
    }
  };

  const btnSearch = () => {
    fnSearchApprovalTemplate(condition, true);
  };

  const handleEditorOnLoad = async () => {
    console.log('handleEditorOnLoad :', editorLoadCount);

    // if (editorLoadCount === 0) editorLoadCount = 1;
    // else {
    //   editorLoadCount = 2;
    //   fnSearchApprovalTemplate(aprTplSearchParamData, true);
    // }
  };

  // 템플릿 목록 조회
  const fnSearchApprovalTemplate = async (params: string, flag: boolean) => {
    await getApprovalTemplate(params).then((result: ApprovalTemplate[]) => {
      setRowData(result);
      if (result && flag) {
        setAprTplInfoData(result[0]);
        // prsDescEditorRef.current?.editorRef.SetBodyValue(result[0].prsDesc ?? '');
        wcstDescEditorRef.current?.editorRef.SetBodyValue(result[0].wcstDesc ?? '');
      }
    });
  };

  const listener = (param: ApprovalTemplate) => {
    // prsDescEditorRef.current?.editorRef.SetBodyValue(param.prsDesc ?? '');
    wcstDescEditorRef.current?.editorRef.SetBodyValue(param.wcstDesc ?? '');
    setAprTplInfoData({ ...param, crudKey: CrudCode.READ });
  };

  const btnNew = () => {
    const newData = rowData.slice();
    newData.splice(0, 0, newRow);
    setRowData(newData);
    setAprTplInfoData(newRow);
    // prsDescEditorRef.current?.editorRef.SetBodyValue(newRow.prsDesc ?? '');
    wcstDescEditorRef.current?.editorRef.SetBodyValue(newRow.wcstDesc ?? '');
  };

  const saveValidation = (saveAprTplInfoData) => {
    if (saveAprTplInfoData?.crudKey === CrudCode.READ) {
      openCommonModal({
        content: t('admin.msg.수정 내역이 없습니다.', '수정 내역이 없습니다.'),
      });
      return false;
    }

    if (!saveAprTplInfoData?.aprTplId) {
      openCommonModal({
        content: t('admin.msg.양식 ID를 입력해주세요.', '양식 ID를 입력해주세요.'),
      });
      return false;
    }

    if (!saveAprTplInfoData?.aprTplNm) {
      openCommonModal({
        content: t('admin.msg.양식명을 입력해주세요.', '양식명을 입력해주세요.'),
      });
      return false;
    }

    if (!saveAprTplInfoData?.aprTplDesc) {
      openCommonModal({
        content: t('admin.msg.양식설명을 입력해주세요.', '양식설명을 입력해주세요.'),
      });
      return false;
    }

    if (
      saveAprTplInfoData?.crudKey === '' ||
      saveAprTplInfoData?.crudKey === null ||
      saveAprTplInfoData?.crudKey === undefined
    ) {
      openCommonModal({
        content: t(
          'admin.msg.저장에 실패하였습니다. 입력값을 확인하세요.',
          '저장에 실패하였습니다. 입력값을 확인하세요.'
        ),
      });
      return false;
    }
    return true;
  };

  const checkNamoModified = () => {
    if (
      // prsDescEditorRef.current?.editorRef.IsDirty() ||
      wcstDescEditorRef.current?.editorRef.IsDirty()
    ) {
      aprTplInfoData.crudKey =
        aprTplInfoData.crudKey === CrudCode.READ ? CrudCode.UPDATE : aprTplInfoData.crudKey;
    }
  };

  const btnSave = async () => {
    checkNamoModified();
    const saveAprTplInfoData = aprTplInfoData;
    // saveAprTplInfoData.prsDesc = prsDescEditorRef.current?.editorRef.GetBodyValue();
    saveAprTplInfoData.wcstDesc = wcstDescEditorRef.current?.editorRef.GetBodyValue();

    if (!saveValidation(saveAprTplInfoData)) return;

    let result: any = null;

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: async () => {
        console.log('saveAprTplInfoData', saveAprTplInfoData);
        saveAprTplInfoData.notfUseYn = saveAprTplInfoData.ntdkId ? 'Y' : 'N';

        if (saveAprTplInfoData?.crudKey === CrudCode.CREATE) {
          result = await createApprovalTemplate(saveAprTplInfoData);
        }
        if (saveAprTplInfoData?.crudKey === CrudCode.UPDATE) {
          result = await modifyApprovalTemplate(saveAprTplInfoData);
        }
        if (result) {
          openMessageBar({
            type: 'confirm',
            content: t('com.label.저장되었습니다.', '저장되었습니다.'),
          });
          fnSearchApprovalTemplate('', false);
          // prsDescEditorRef.current?.editorRef.SetDirty();
          wcstDescEditorRef.current?.editorRef.SetDirty();
          setAprTplInfoData({ ...aprTplInfoData, crudKey: CrudCode.READ });
        } else {
          openCommonModal({
            content: t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
          });
        }
      },
    });
  };

  const handleAprTplId = (event: any) => {
    const value = event?.target.value;
    const regex = /^[A-Za-z0-9-]*$/;
    if (value.length <= 13 && regex.test(value)) {
      handleChange(event);
    } else {
      openMessageBar({
        type: 'error',
        content: t(
          'admin.msg.양식 ID 규칙에 위배됩니다.(영문 및 - 만 입력가능, 13글자 이하)',
          '양식 ID 규칙에 위배됩니다.(영문 및 - 만 입력가능, 13글자 이하)'
        ),
      });
    }
  };

  const handleItemChange = (name: string, value: string | string[]) => {
    setAprTplInfoData((prev) => ({
      ...prev,
      [name]: value,
      crudKey: prev.crudKey === CrudCode.CREATE ? CrudCode.CREATE : CrudCode.UPDATE,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const handleChange = (event: any) => {
    setAprTplInfoData((prevData) => {
      return {
        ...prevData,
        [event?.target.name]: event?.target.value,
        crudKey: prevData.crudKey === CrudCode.CREATE ? CrudCode.CREATE : CrudCode.UPDATE,
      };
    });
  };

  // <Button className="buttonFind" disableRipple></Button>
  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{t('admin.label.양식명', '양식명')}</label>
                <CustomInputWithSearch
                  value={condition}
                  onKeyDown={handleKeyDown}
                  placeholder={String(
                    t('admin.label.양식명을 입력해 주세요.', '양식명을 입력해 주세요.')
                  )}
                  onChange={(e) => setCondition(e.target.value)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button css={IconButton.button} className="reload" disableRipple onClick={btnReload} />
            <Button css={IconButton.button} className="find" disableRipple onClick={btnSearch}>
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>

      <ContentFlex>
        <ContentSection className="section width50p marginT0">
          <ApprovalTemplateManagementGrid
            aprTplNm={aprTplSearchParamData}
            rowData={rowData}
            callBackRowData={listener}
            editable={false}
          />
          <GlobalBtnGroup style={{ marginTop: '8px' }}>
            <Button css={IconButton.button} className="write" disableRipple onClick={btnNew}>
              {t('com.button.신규', '신규')}
            </Button>
          </GlobalBtnGroup>
        </ContentSection>
        <ContentSection className="section width50p marginT0">
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('admin.label.결재양식 기본정보', '결재양식 기본정보')}</h3>
            </SubTitleGroup>
          </SubTitleLayout>
          <TableContainer css={tb.table}>
            <Table>
              <TableBody>
                <colgroup>
                  <col width="20%" />
                  <col />
                  <col width="20%" />
                  <col />
                </colgroup>
                <TableRow>
                  <TableCell>
                    <span>{t('admin.label.양식ID', '양식ID')}</span>
                  </TableCell>
                  <TableCell>
                    <CustomInputWrap>
                      <CustomInputText
                        placeholder={String(
                          t('admin.label.양식ID를 입력하세요.', '양식ID를 입력하세요.')
                        )}
                        name="aprTplId"
                        value={aprTplInfoData?.aprTplId}
                        onChange={handleAprTplId}
                        readOnly={aprTplInfoData?.crudKey !== CrudCode.CREATE}
                        maxLength={13}
                      />
                    </CustomInputWrap>
                  </TableCell>
                  <TableCell>
                    <span>{t('admin.label.전자결재유형', '전자결재유형')}</span>
                  </TableCell>
                  <TableCell>
                    <ComboBox
                      placeholder={String(t('com.label.선택', '선택'))}
                      options={code?.intgAprTpCdData}
                      defaultValue={aprTplInfoData?.intgAprTpCd}
                      onChange={(value) => {
                        handleItemChange('intgAprTpCd', value);
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <span>{t('admin.label.양식명', '양식명')}</span>
                  </TableCell>
                  <TableCell colSpan={3}>
                    <CustomInputWrap>
                      <CustomInputText
                        type="text"
                        name="aprTplNm"
                        placeholder={String(
                          t(
                            'admin.label.양식명을 입력해 주세요.',
                            'admin.label.양식명을 입력해 주세요.'
                          )
                        )}
                        // disabled
                        value={CommonUtil.safeHtmlContents(aprTplInfoData?.aprTplNm, false)}
                        onChange={(value) => handleChange(value)}
                      ></CustomInputText>
                    </CustomInputWrap>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <span>{t('admin.label.정렬순서', '정렬순서')}</span>
                  </TableCell>
                  <TableCell>
                    <CustomInputWrap>
                      <CustomInputText
                        name="sortOrd"
                        value={aprTplInfoData?.sortOrd}
                        type="number"
                        onChange={(event: any) => {
                          const value = event?.target.value;
                          const regex = /^[0-9]*$/;
                          if (value < 0 && !regex.test(value)) {
                            openCommonModal({
                              content: t(
                                'admin.msg.정렬 순서는 음수일 수 없습니다.',
                                '정렬 순서는 음수일 수 없습니다.'
                              ),
                            });
                            return;
                          }
                          handleChange(event);
                        }}
                      />
                    </CustomInputWrap>
                  </TableCell>
                  <TableCell>
                    <span>{t('admin.label.사용여부', '사용여부')}</span>
                  </TableCell>
                  <TableCell>
                    <ComboBox
                      options={code?.useYn}
                      defaultValue={aprTplInfoData?.useYn}
                      placeholder={String(t('admin.label.선택', '선택'))}
                      onChange={(value) => handleItemChange('useYn', value)}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <span>{t('admin.label.결재라인', '결재라인')}</span>
                  </TableCell>
                  <TableCell>
                    <ComboBox
                      options={selectAprRuleId}
                      defaultValue={aprTplInfoData?.aprRuleId}
                      placeholder={String(t('admin.label.사용안함', '사용안함'))}
                      onChange={(value) => {
                        handleItemChange('aprRuleId', value);
                        // 통보처를 사용하는 경우 결재규칙에 있는 통보처 사용안함.
                        // if (value !== '') {
                        //   handleItemChange('ntdkId', '');
                        // }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <span>{t('admin.label.통보처', '통보처')}</span>
                  </TableCell>
                  <TableCell>
                    <ComboBox
                      // disabled={aprTplInfoData?.aprRuleId !== ''}
                      options={code?.notificationGroups}
                      defaultValue={aprTplInfoData?.ntdkId.toString()}
                      placeholder={String(t('admin.label.사용안함', '사용안함'))}
                      onChange={(value) => handleItemChange('ntdkId', value)}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <span>{t('admin.label.양식설명', '양식설명')}</span>
                  </TableCell>
                  <TableCell colSpan={3}>
                    <CustomTextarea
                      className="fullWidth"
                      placeholder={String(
                        t('admin.label.양식설명을 입력해주세요.', '양식설명을 입력해주세요.')
                      )}
                      name="aprTplDesc"
                      value={aprTplInfoData?.aprTplDesc ? aprTplInfoData.aprTplDesc : ''}
                      onChange={(event: any) => {
                        const value = event?.target.value;
                        if (value.length <= 65) {
                          handleChange(event);
                        } else {
                          openCommonModal({
                            content: t(
                              'admin.msg.양식설명은 65자를 넘을 수 없습니다.',
                              '양식설명은 65자를 넘을 수 없습니다.'
                            ),
                          });
                        }
                      }}
                      maxLength={65}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <span>{t('admin.label.결재제목', '결재제목')}</span>
                  </TableCell>
                  <TableCell colSpan={3}>
                    <CustomInputWrap>
                      <CustomInputText
                        type="text"
                        name="prsDesc"
                        placeholder={String(
                          t('admin.label.결재제목을 입력해 주세요.', '결재제목을 입력해 주세요.')
                        )}
                        // disabled
                        value={CommonUtil.safeHtmlContents(aprTplInfoData?.prsDesc, false)}
                        onChange={(value) => handleChange(value)}
                      />
                    </CustomInputWrap>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <span>{t('admin.label.결재설명', '결재설명')}</span>
                  </TableCell>
                  <TableCell colSpan={3}>
                    <CrossEditor
                      ref={wcstDescEditorRef}
                      name="wcstDesc"
                      params={{ Width: '100%', Height: 300, Chevron: true }}
                      value={aprTplInfoData?.wcstDesc}
                      onLoaded={handleEditorOnLoad}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <GlobalBtnGroup>
            <Button css={IconButton.button} className="save" disableRipple onClick={btnSave}>
              {t('com.button.저장', '저장')}
            </Button>
          </GlobalBtnGroup>
        </ContentSection>
      </ContentFlex>
    </>
  );
};
